import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from '@repo/design-system-kit';
import { IconCheckmarkRoundedOutlined } from '@repo/monochrome-icons';
import cx from 'clsx';
import { Container } from '../container/container';
import accountAlmostReadyIllustration from '../../assets/account-almost-ready.svg';
import styles from './reassurance.module.css';

interface ReassuranceItem {
  id: string;
  label: ReactNode;
}

interface ReassuranceProps {
  firstName: string;
  onContinue: () => void;
  items?: ReassuranceItem[];
}

const defaultItems: ReassuranceItem[] = [
  {
    id: 'item1',
    label: <FormattedMessage id="customer-registration-kit.business-account.reassurance.item1" />,
  },
  {
    id: 'item2',
    label: <FormattedMessage id="customer-registration-kit.business-account.reassurance.item2" />,
  },
  {
    id: 'item3',
    label: <FormattedMessage id="customer-registration-kit.business-account.reassurance.item3" />,
  },
  {
    id: 'item4',
    label: <FormattedMessage id="customer-registration-kit.business-account.reassurance.item4" />,
  },
];

export function Reassurance({ firstName, onContinue, items = defaultItems }: ReassuranceProps) {
  return (
    <Container>
      <div className={styles.content}>
        <img
          alt=""
          className={styles.illustration}
          role="presentation"
          src={accountAlmostReadyIllustration}
        />

        <h1 className={styles.title} data-test-title>
          <FormattedMessage
            id="customer-registration-kit.business-account.reassurance.title"
            values={{ name: firstName }}
          />
        </h1>

        <div className={cx('body-standard', styles.subtitle)} data-test-subtitle>
          <FormattedMessage id="customer-registration-kit.business-account.reassurance.subtitle" />
        </div>

        <ul className={styles.list}>
          {items.map((item, index) => (
            <li className={styles.listItem} data-test-step={index} key={item.id}>
              <IconCheckmarkRoundedOutlined aria-hidden="true" className={styles.icon} />
              <span className={styles.itemText}>{item.label}</span>
            </li>
          ))}
        </ul>

        <div>
          <Button data-test-continue-button onPress={onContinue}>
            <FormattedMessage id="customer-registration-kit.generic.continue.cta" />
          </Button>
        </div>
      </div>
    </Container>
  );
}
